import { useCallback, useEffect, useRef, useState } from "react";

import QUERY_TAGS from "../search/queryTags";
import { invalidateTags } from "../search/queryCache";
import { evaluatePayoutFee, requestPayout } from "./financeService";
import { mapPayout, toFeeQuote } from "./financeMappers";

export const FEE_DEBOUNCE_MS = 350;

const useWithdrawalRequest = ({ transferRecipientId, amount, currency } = {}) => {
  const [quote, setQuote] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [feeError, setFeeError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const ticketRef = useRef(0);

  const ready = Boolean(transferRecipientId) && Number(amount) > 0;

  useEffect(() => {
    if (!ready) {
      setQuote(null);
      setFeeError(null);
      setEvaluating(false);

      return undefined;
    }

    ticketRef.current += 1;

    const ticket = ticketRef.current;

    setEvaluating(true);

    const timer = setTimeout(async () => {
      try {
        const data = await evaluatePayoutFee({
          transferRecipientId,
          value: amount,
          currency,
        });

        if (ticketRef.current !== ticket) return;

        setQuote(toFeeQuote(data, { value: amount, currency }));
        setFeeError(null);
      } catch (error) {
        if (ticketRef.current !== ticket) return;

        setQuote(null);
        setFeeError(error);
      } finally {
        if (ticketRef.current === ticket) setEvaluating(false);
      }
    }, FEE_DEBOUNCE_MS);

    return () => {
      ticketRef.current += 1;
      clearTimeout(timer);
    };
  }, [ready, transferRecipientId, amount, currency]);

  const submit = useCallback(async () => {
    if (!ready) return { status: "invalid" };

    setSubmitting(true);

    try {
      const data = await requestPayout({ transferRecipientId, value: amount, currency });

      invalidateTags([
        QUERY_TAGS.PAYOUT_LIST,
        QUERY_TAGS.PAYMENT_WALLET,
        QUERY_TAGS.PAYMENT_TRANSACTIONS,
      ]);

      return { status: "requested", payout: mapPayout(data) };
    } catch (error) {
      return { status: "error", error };
    } finally {
      setSubmitting(false);
    }
  }, [ready, transferRecipientId, amount, currency]);

  return { quote, evaluating, feeError, submitting, submit };
};

export default useWithdrawalRequest;
